import { Opt } from '../fundo_liquido/mock';

export type CampoTipo =
  | 'text' | 'number' | 'date' | 'select' | 'textarea'
  | 'checkbox' | 'money' | 'percent' | 'entidade' | 'cnpj';

export interface CampoDef {
  nome: string;
  label: string;
  tipo: CampoTipo;
  obrigatorio?: boolean;
  opcoes?: Opt[];                       // para tipo 'select'
  entidade?: 'emissor' | 'prestador' | 'fundo';   // para tipo 'entidade'
  hint?: string;
  full?: boolean;
  escala?: number;
  default?: string;
  habilitarQuando?: { campo: string; valor: string | string[] };
}

export interface SecaoDef {
  id: string;
  titulo: string;
  destino: 'renda_fixa' | 'detalhe';
  campos: CampoDef[];
}

export interface TipoAtivoDef {
  tipo: string;
  grupo: 'RENDA_FIXA' | 'RENDA_VARIAVEL' | 'DERIVATIVO' | 'FUNDO';
  grupoLabel: string;
  label: string;
  familiaRF: boolean;
  secoes: SecaoDef[];
  emissorLabel?: string;                               // rótulo do emissor no topo; default "Emissor"
  emissorObrigatorio?: boolean;
  semEmissor?: boolean;
}

const o = (value: string, label: string): Opt => ({ value, label });

const INDEXADOR = [o('CDI', 'CDI'), o('IPCA', 'IPCA'), o('IGPM', 'IGP-M'), o('PRE', 'Pré-fixado'), o('SELIC', 'SELIC')];
const MOEDA = [o('BRL', 'BRL'), o('USD', 'USD')];
const BASE_CALCULO = [o('252', '252 (dias úteis)'), o('360', '360 (corridos)')];
const PERIODICIDADE = [
  o('VENCIMENTO', 'No vencimento'), o('MENSAL', 'Mensal'), o('TRIMESTRAL', 'Trimestral'),
  o('SEMESTRAL', 'Semestral'), o('ANUAL', 'Anual'),
];
const AMORTIZACAO = [o('BULLET', 'Bullet (no vencimento)'), o('SAC', 'SAC'), o('PRICE', 'Price'), o('CUSTOMIZADA', 'Customizada')];
const AGENCIA_RATING = [
  o('SP', 'S&P'), o('FITCH', 'Fitch'), o('MOODYS', "Moody's"),
  o('AUSTIN', 'Austin Rating'), o('LIBERUM', 'Liberum'), o('SR_RATING', 'SR Rating'),
];
const PUBLICO_ALVO = [o('PROFISSIONAL', 'Investidor Profissional'), o('QUALIFICADO', 'Investidor Qualificado'), o('GERAL', 'Público Geral')];

const CRI_CRA_TIPO = [o('CRI', 'CRI'), o('CRA', 'CRA')];
const LASTRO_CRICRA = [
  o('CCI', 'CCI'), o('CCB', 'CCB'), o('DEBENTURES', 'Debêntures'),
  o('CPR', 'CPR'), o('CDA', 'CDA'), o('WA', 'WA'), o('CDCA', 'CDCA'), o('LCA', 'LCA'),
];
const DEB_NATUREZA = [o('CORPORATIVO', 'Corporativo'), o('SECURITIZACAO', 'Securitização')];
const DEB_TIPO = [o('SIMPLES', 'Simples'), o('CONVERSIVEL', 'Conversível em ações')];
const DEB_ESPECIE = [
  o('GARANTIA_REAL', 'Com garantia real'), o('FLUTUANTE', 'Flutuante'),
  o('QUIROGRAFARIA', 'Quirografária'), o('SUBORDINADA', 'Subordinada'),
];
const DEB_FORMA = [o('ESCRITURAL', 'Escritural'), o('CERTIFICADA', 'Certificada')];
const LCI_TIPO = [o('LCI', 'LCI'), o('LCA', 'LCA')];
const LCI_LIQUIDEZ = [
  o('VENCIMENTO', 'No vencimento'), o('DIARIA_POS_CARENCIA', 'Diária após carência'),
  o('MENSAL_POS_CARENCIA', 'Mensal após carência'),
];
const CDB_TIPO = [o('CDB', 'CDB'), o('LF', 'LF')];
const CDB_SUBORD = [o('NAO_SUBORDINADA', 'Não subordinada'), o('NIVEL_1', 'Nível 1'), o('NIVEL_2', 'Nível 2')];
const CDB_LIQUIDEZ = [o('VENCIMENTO', 'No vencimento'), o('DIARIA', 'Diária (CDB)'), o('NAO_PERMITIDA', 'Não permitida (LF)')];
const TP_TIPO = [o('LTN', 'LTN'), o('NTN_B', 'NTN-B'), o('NTN_B_PRINCIPAL', 'NTN-B Principal'), o('NTN_F', 'NTN-F'), o('LFT', 'LFT')];
const LAM_LIQUIDEZ = [o('VENCIMENTO', 'No vencimento'), o('DIARIA_POS_CARENCIA', 'Diária após carência'), o('OUTRA', 'Outra')];
const ACAO_TIPO = [
  o('ON', 'ON (Ordinária)'), o('PN', 'PN (Preferencial)'), o('UNIT', 'Unit'),
  o('BDR_N1', 'BDR Nível I'), o('BDR_N2', 'BDR Nível II'), o('BDR_N3', 'BDR Nível III'),
  o('BDR_NAO_PATROCINADO', 'BDR Não Patrocinado'),
];
const ACAO_GOV = [
  o('NOVO_MERCADO', 'Novo Mercado'), o('NIVEL_2', 'Nível 2'), o('NIVEL_1', 'Nível 1'),
  o('BOVESPA_MAIS', 'Bovespa Mais'), o('BOVESPA_MAIS_N2', 'Bovespa Mais Nível 2'),
  o('TRADICIONAL', 'Tradicional'), o('NAO_LISTADO', 'Não listado'),
];
const ACAO_MERCADO = [o('A_VISTA', 'À Vista'), o('FRACIONARIO', 'Fracionário'), o('BDR', 'BDR')];
const FUT_TIPO = [
  o('JUROS', 'Juros'), o('CAMBIO', 'Câmbio'), o('INDICE_ACOES', 'Índice de Ações'),
  o('COMMODITY', 'Commodity'), o('OUTROS', 'Outros'),
];
const FUNDO_ORIGEM = [o('INTERNO', 'Interno (cadastrado)'), o('EXTERNO', 'Externo (terceiros)')];
const CLASSE_CVM = [
  o('RENDA_FIXA', 'Renda Fixa'), o('MULTIMERCADO', 'Multimercado'), o('ACOES', 'Ações'),
  o('CAMBIAL', 'Cambial'), o('FIDC', 'FIDC'), o('FII', 'FII'),
];
const TIPO_COTA = [o('abertura', 'Abertura (D0)'), o('fechamento', 'Fechamento (D+1)')];
const CONDOMINIO = [o('ABERTO', 'Aberto'), o('FECHADO', 'Fechado')];
const STATUS_APLIC = [o('ABERTO', 'Aberto'), o('FECHADO', 'Fechado'), o('ABERTURA', 'Em abertura')];
const MASTER_FIC = [o('MASTER', 'Master'), o('FIC', 'FIC')];
const CALENDARIO = [o('ANBIMA', 'ANBIMA'), o('B3', 'B3'), o('CORRIDOS', 'Dias corridos')];
const REGIME_JUROS = [o('SIMPLES', 'Simples'), o('COMPOSTOS', 'Compostos')];

const RF_DATAS: SecaoDef = {
  id: 'rf_datas', titulo: 'Datas', destino: 'renda_fixa',
  campos: [
    { nome: 'data_emissao', label: 'Data de emissão', tipo: 'date', obrigatorio: true },
    { nome: 'data_inicio_accrual', label: 'Data início accrual', tipo: 'date' },
    { nome: 'data_vencimento', label: 'Data de vencimento', tipo: 'date' },
    { nome: 'amortizacao', label: 'Amortização', tipo: 'select', opcoes: AMORTIZACAO },
    { nome: 'periodicidade_juros', label: 'Periodicidade dos juros', tipo: 'select', opcoes: PERIODICIDADE },
  ],
};

const RF_INDEXACAO: SecaoDef = {
  id: 'rf_indexacao', titulo: 'Indexação', destino: 'renda_fixa',
  campos: [
    { nome: 'indexador', label: 'Indexador', tipo: 'select', opcoes: INDEXADOR },
    { nome: 'percentual_indexador', label: '% do indexador', tipo: 'percent', escala: 6, hint: 'Ex.: 110 para 110% do CDI' },
    { nome: 'spread', label: 'Spread (% a.a.)', tipo: 'percent', escala: 6, hint: 'Ex.: 2,5 para IPCA+2,5%' },
    { nome: 'taxa_prefixada', label: 'Taxa pré-fixada (% a.a.)', tipo: 'percent', escala: 6,
      habilitarQuando: { campo: 'indexador', valor: 'PRE' } },
    { nome: 'base_calculo', label: 'Base de cálculo', tipo: 'select', opcoes: BASE_CALCULO },
  ],
};

const RF_RISCO: SecaoDef = {
  id: 'rf_risco', titulo: 'Risco', destino: 'renda_fixa',
  campos: [
    { nome: 'rating', label: 'Rating', tipo: 'text' },
    { nome: 'agencia_rating', label: 'Agência de rating', tipo: 'select', opcoes: AGENCIA_RATING },
    { nome: 'agente_fiduciario_id', label: 'Agente fiduciário', tipo: 'entidade', entidade: 'prestador' },
    { nome: 'publico_alvo', label: 'Público-alvo', tipo: 'select', opcoes: PUBLICO_ALVO },
  ],
};

const RF_CADASTRO: SecaoDef = {
  id: 'rf_cadastro', titulo: 'Cadastro', destino: 'renda_fixa',
  campos: [
    { nome: 'codigo_cetip_b3', label: 'Código CETIP / B3', tipo: 'text' },
    { nome: 'isin', label: 'ISIN', tipo: 'text' },
    { nome: 'valor_emissao', label: 'Valor de emissão (VNA)', tipo: 'money', escala: 6 },
    { nome: 'moeda', label: 'Moeda', tipo: 'select', opcoes: MOEDA, default: 'BRL' },
    { nome: 'banco_liquidante_id', label: 'Banco liquidante', tipo: 'entidade', entidade: 'prestador' },
    { nome: 'conta_b3_cetip', label: 'Conta B3 / CETIP', tipo: 'text' },
    { nome: 'carencia_dias', label: 'Carência (dias)', tipo: 'number' },
    { nome: 'garantias', label: 'Garantias', tipo: 'textarea', full: true },
    { nome: 'isento_ir_pf', label: 'Isento de IR (PF)', tipo: 'checkbox',
      hint: 'Aplica a CRI/CRA e debêntures de infraestrutura (incentivadas)' },
  ],
};

const RF_SECOES: SecaoDef[] = [RF_DATAS, RF_INDEXACAO, RF_RISCO, RF_CADASTRO];

const CRI_CRA: TipoAtivoDef = {
  tipo: 'CRI_CRA', grupo: 'RENDA_FIXA', grupoLabel: 'Renda Fixa', label: 'CRI / CRA',
  familiaRF: true, emissorLabel: 'Securitizadora',
  secoes: [
    ...RF_SECOES,
    {
      id: 'cri_cra', titulo: 'CRI / CRA', destino: 'detalhe',
      campos: [
        { nome: 'tipo', label: 'Tipo', tipo: 'select', opcoes: CRI_CRA_TIPO, obrigatorio: true },
        { nome: 'serie', label: 'Série', tipo: 'text', obrigatorio: true },
        { nome: 'emissao_num', label: 'Emissão nº', tipo: 'text', obrigatorio: true },
        { nome: 'devedor_id', label: 'Devedor principal', tipo: 'entidade', entidade: 'emissor', obrigatorio: true, hint: 'Empresa cedente dos recebíveis' },
        { nome: 'tipo_lastro', label: 'Tipo de lastro', tipo: 'select', opcoes: LASTRO_CRICRA, obrigatorio: true },
        { nome: 'escriturador_id', label: 'Escriturador', tipo: 'entidade', entidade: 'prestador' },
        { nome: 'agente_depositario_id', label: 'Agente depositário', tipo: 'entidade', entidade: 'prestador' },
        { nome: 'regime_fiduciario', label: 'Regime fiduciário', tipo: 'checkbox' },
      ],
    },
  ],
};

const DEBENTURE: TipoAtivoDef = {
  tipo: 'DEBENTURE', grupo: 'RENDA_FIXA', grupoLabel: 'Renda Fixa', label: 'Debênture',
  familiaRF: true, emissorLabel: 'Companhia emissora',
  secoes: [
    ...RF_SECOES,
    {
      id: 'debenture', titulo: 'Debênture', destino: 'detalhe',
      campos: [
        { nome: 'natureza', label: 'Natureza', tipo: 'select', opcoes: DEB_NATUREZA, obrigatorio: true },
        { nome: 'tipo', label: 'Tipo', tipo: 'select', opcoes: DEB_TIPO,
          hint: 'Conversível só se aplica a debênture corporativa',
          habilitarQuando: { campo: 'natureza', valor: 'CORPORATIVO' } },
        { nome: 'especie', label: 'Espécie', tipo: 'select', opcoes: DEB_ESPECIE, obrigatorio: true },
        { nome: 'serie', label: 'Série', tipo: 'text', obrigatorio: true },
        { nome: 'emissao_num', label: 'Emissão nº', tipo: 'text', obrigatorio: true },
        { nome: 'escriturador_id', label: 'Escriturador', tipo: 'entidade', entidade: 'prestador' },
        { nome: 'coordenador_lider', label: 'Coordenador líder', tipo: 'text' },
        { nome: 'forma', label: 'Forma', tipo: 'select', opcoes: DEB_FORMA, obrigatorio: true },
        { nome: 'incentivada', label: 'Incentivada (Lei 12.431)', tipo: 'checkbox' },
      ],
    },
  ],
};

const LCI_LCA: TipoAtivoDef = {
  tipo: 'LCI_LCA', grupo: 'RENDA_FIXA', grupoLabel: 'Renda Fixa', label: 'LCI / LCA',
  familiaRF: true, emissorLabel: 'Banco emissor',
  secoes: [
    ...RF_SECOES,
    {
      id: 'lci_lca', titulo: 'LCI / LCA', destino: 'detalhe',
      campos: [
        { nome: 'tipo', label: 'Tipo', tipo: 'select', opcoes: LCI_TIPO, obrigatorio: true },
        { nome: 'lastro', label: 'Lastro imobiliário / agrícola', tipo: 'text' },
        { nome: 'liquidez', label: 'Liquidez', tipo: 'select', opcoes: LCI_LIQUIDEZ, obrigatorio: true },
        { nome: 'cobertura_fgc', label: 'Cobertura FGC', tipo: 'checkbox' },
      ],
    },
  ],
};

const CDB_LF: TipoAtivoDef = {
  tipo: 'CDB_LF', grupo: 'RENDA_FIXA', grupoLabel: 'Renda Fixa', label: 'CDB / LF',
  familiaRF: true, emissorLabel: 'Banco emissor',
  secoes: [
    ...RF_SECOES,
    {
      id: 'cdb_lf', titulo: 'CDB / LF', destino: 'detalhe',
      campos: [
        { nome: 'tipo', label: 'Tipo', tipo: 'select', opcoes: CDB_TIPO, obrigatorio: true },
        { nome: 'subordinacao_lf', label: 'Subordinação (LF)', tipo: 'select', opcoes: CDB_SUBORD, hint: 'Somente para LF' },
        { nome: 'liquidez', label: 'Liquidez / resgate antecipado', tipo: 'select', opcoes: CDB_LIQUIDEZ, obrigatorio: true },
        { nome: 'cobertura_fgc', label: 'Cobertura FGC', tipo: 'checkbox', hint: 'CDB tem FGC; LF não' },
      ],
    },
  ],
};

const TITULO_PUBLICO: TipoAtivoDef = {
  tipo: 'TITULO_PUBLICO', grupo: 'RENDA_FIXA', grupoLabel: 'Renda Fixa', label: 'Título Público',
  familiaRF: true, emissorLabel: 'Emissor', emissorObrigatorio: false,
  secoes: [
    ...RF_SECOES,
    {
      id: 'titulo_publico', titulo: 'Título Público', destino: 'detalhe',
      campos: [
        { nome: 'tipo', label: 'Tipo', tipo: 'select', opcoes: TP_TIPO, obrigatorio: true },
        { nome: 'codigo_selic', label: 'Código SELIC', tipo: 'text', obrigatorio: true },
        { nome: 'cupom_semestral', label: 'Cupom semestral', tipo: 'checkbox', hint: 'NTN-B / NTN-F' },
      ],
    },
  ],
};

const LAM: TipoAtivoDef = {
  tipo: 'LAM', grupo: 'RENDA_FIXA', grupoLabel: 'Renda Fixa', label: 'LAM',
  familiaRF: true, emissorLabel: 'Arrendador / emissora',
  secoes: [
    ...RF_SECOES,
    {
      id: 'lam', titulo: 'LAM', destino: 'detalhe',
      campos: [
        { nome: 'tipo_lastro', label: 'Tipo de lastro', tipo: 'text', hint: 'Contrato de arrendamento vinculado' },
        { nome: 'liquidez', label: 'Liquidez', tipo: 'select', opcoes: LAM_LIQUIDEZ, obrigatorio: true },
        { nome: 'cobertura_fgc', label: 'Cobertura FGC', tipo: 'checkbox' },
      ],
    },
  ],
};

const ACAO: TipoAtivoDef = {
  tipo: 'ACAO', grupo: 'RENDA_VARIAVEL', grupoLabel: 'Renda Variável', label: 'Ação',
  familiaRF: false, emissorLabel: 'Companhia',
  secoes: [
    {
      id: 'acao', titulo: 'Ação', destino: 'detalhe',
      campos: [
        { nome: 'ticker_b3', label: 'Ticker B3', tipo: 'text', obrigatorio: true, hint: 'Ex.: PETR4, VALE3' },
        { nome: 'isin', label: 'ISIN', tipo: 'text', obrigatorio: true },
        { nome: 'tipo_acao', label: 'Tipo de ação', tipo: 'select', opcoes: ACAO_TIPO, obrigatorio: true },
        { nome: 'classe', label: 'Classe', tipo: 'text', hint: 'PN: PNA, PNB...' },
        { nome: 'setor_b3', label: 'Setor B3', tipo: 'text', obrigatorio: true },
        { nome: 'subsetor_b3', label: 'Subsetor B3', tipo: 'text' },
        { nome: 'segmento_b3', label: 'Segmento B3', tipo: 'text' },
        { nome: 'segmento_governanca', label: 'Segmento de governança', tipo: 'select', opcoes: ACAO_GOV, obrigatorio: true },
        { nome: 'moeda_cotacao', label: 'Moeda de cotação', tipo: 'select', opcoes: MOEDA, obrigatorio: true, default: 'BRL' },
        { nome: 'mercado', label: 'Mercado', tipo: 'select', opcoes: ACAO_MERCADO, obrigatorio: true },
        { nome: 'indices_composicao', label: 'Índices de composição', tipo: 'text', hint: 'Separe por vírgula (ex.: IBOV, SMLL, IDIV)' },
        { nome: 'free_float', label: 'Free float (%)', tipo: 'percent', escala: 6 },
        { nome: 'custodiante_id', label: 'Custodiante', tipo: 'entidade', entidade: 'prestador' },
        { nome: 'conta_b3', label: 'Conta B3', tipo: 'text' },
        { nome: 'lote_padrao', label: 'Lote padrão', tipo: 'number', hint: 'Geralmente 100' },
        { nome: 'tag_along', label: 'Tag along (%)', tipo: 'percent', escala: 6 },
        { nome: 'direito_voto', label: 'Direito a voto', tipo: 'checkbox' },
      ],
    },
  ],
};

const FUTURO: TipoAtivoDef = {
  tipo: 'FUTURO', grupo: 'DERIVATIVO', grupoLabel: 'Derivativo', label: 'Futuro',
  familiaRF: false, semEmissor: true,
  secoes: [
    {
      id: 'futuro', titulo: 'Contrato futuro', destino: 'detalhe',
      campos: [
        { nome: 'simbolo_b3', label: 'Símbolo / Código B3', tipo: 'text', obrigatorio: true, hint: 'Ex.: DI1, DOL, WIN, IND' },
        { nome: 'descricao', label: 'Descrição', tipo: 'text', obrigatorio: true, full: true },
        { nome: 'tipo', label: 'Tipo', tipo: 'select', opcoes: FUT_TIPO, obrigatorio: true },
        { nome: 'subtipo', label: 'Subtipo', tipo: 'text', hint: 'Ex.: DI1, DAP, DOL, WIN, BGI...' },
        { nome: 'ativo_subjacente', label: 'Ativo subjacente', tipo: 'text', obrigatorio: true },
        { nome: 'tamanho_contrato', label: 'Tamanho do contrato', tipo: 'money', escala: 2, obrigatorio: true },
        { nome: 'moeda_cotacao', label: 'Moeda de cotação', tipo: 'select', opcoes: MOEDA, obrigatorio: true, default: 'BRL' },
        { nome: 'unidade_cotacao', label: 'Unidade de cotação', tipo: 'text', obrigatorio: true },
        { nome: 'tick_size', label: 'Tick size (variação mínima)', tipo: 'text', obrigatorio: true },
        { nome: 'valor_ponto', label: 'Valor do ponto (R$)', tipo: 'money', escala: 6, obrigatorio: true },
        { nome: 'codigo_vencimento', label: 'Código de vencimento', tipo: 'text', obrigatorio: true, hint: 'Letra do mês + 2 dígitos do ano' },
        { nome: 'vencimento_ativo', label: 'Vencimento', tipo: 'date', obrigatorio: true },
        { nome: 'ultimo_dia_negociacao', label: 'Último dia de negociação', tipo: 'date', obrigatorio: true },
        { nome: 'margem_garantia', label: 'Margem de garantia', tipo: 'text' },
        { nome: 'camara_compensacao', label: 'Câmara de compensação', tipo: 'text', default: 'B3' },
        { nome: 'horario_negociacao', label: 'Horário de negociação', tipo: 'text', obrigatorio: true },
        { nome: 'codigo_bloomberg', label: 'Código Bloomberg', tipo: 'text' },
        { nome: 'codigo_reuters', label: 'Código Reuters / Refinitiv', tipo: 'text' },
        { nome: 'par_moedas', label: 'Par de moedas', tipo: 'text', hint: 'Câmbio (ex.: USD/BRL)' },
        { nome: 'taxa_referencia', label: 'Taxa de referência', tipo: 'text', hint: 'Ex.: DI over, PTAX' },
        { nome: 'indice_referencia', label: 'Índice de referência', tipo: 'text', hint: 'Ex.: Ibovespa, IBRX-50' },
        { nome: 'ajuste_diario', label: 'Ajuste diário', tipo: 'checkbox' },
      ],
    },
  ],
};

// helper: marca um campo como "só quando externo"
const ext = (c: CampoDef): CampoDef => ({ ...c, habilitarQuando: { campo: 'origem', valor: 'EXTERNO' } });

const FUNDO: TipoAtivoDef = {
  tipo: 'FUNDO', grupo: 'RENDA_VARIAVEL', grupoLabel: 'Renda Variável', label: 'Fundo / FIDC',
  familiaRF: false, semEmissor: true,
  secoes: [
    {
      id: 'fundo', titulo: 'Fundo', destino: 'detalhe',
      campos: [
        { nome: 'origem', label: 'Origem', tipo: 'select', opcoes: FUNDO_ORIGEM, obrigatorio: true, default: 'INTERNO' },
        // interno: referencia o fundo cadastrado
        { nome: 'fundo_id', label: 'Fundo', tipo: 'entidade', entidade: 'fundo', obrigatorio: true,
          habilitarQuando: { campo: 'origem', valor: 'INTERNO' }, hint: 'Fundo já cadastrado na plataforma' },
        // externo: cadastro completo (Fundos GERAL do documento)
        ext({ nome: 'cnpj', label: 'CNPJ do fundo', tipo: 'cnpj', obrigatorio: true }),
        ext({ nome: 'razao_social', label: 'Razão social', tipo: 'text', obrigatorio: true }),
        ext({ nome: 'nome_relatorio', label: 'Nome relatório', tipo: 'text' }),
        ext({ nome: 'codigo_anbima', label: 'Código ANBIMA', tipo: 'text' }),
        ext({ nome: 'moeda', label: 'Moeda', tipo: 'select', opcoes: MOEDA, default: 'BRL' }),
        ext({ nome: 'tipo_cota', label: 'Tipo de cota', tipo: 'select', opcoes: TIPO_COTA }),
        ext({ nome: 'master_fic', label: 'Master / FIC', tipo: 'select', opcoes: MASTER_FIC }),
        ext({ nome: 'valor_primeira_cota', label: 'Valor da 1ª cota', tipo: 'money', escala: 6 }),
        ext({ nome: 'condominio', label: 'Condomínio', tipo: 'select', opcoes: CONDOMINIO }),
        ext({ nome: 'classe_cvm', label: 'Classe CVM', tipo: 'select', opcoes: CLASSE_CVM }),
        ext({ nome: 'classe_anbima', label: 'Classe ANBIMA', tipo: 'text' }),
        ext({ nome: 'subclasse', label: 'Subclasse (FIDC)', tipo: 'text', hint: 'Sênior / Mezanino / Subordinada / Única' }),
        ext({ nome: 'tx_administracao', label: 'Taxa de administração', tipo: 'text', hint: 'Como no regulamento (ex.: 0,80% a.a.)' }),
        ext({ nome: 'tx_custodia', label: 'Taxa de custódia', tipo: 'text' }),
        ext({ nome: 'tx_performance', label: 'Taxa de performance', tipo: 'text' }),
        ext({ nome: 'tx_adm_minima', label: 'Taxa adm mínima (% a.a.)', tipo: 'percent', escala: 8 }),
        ext({ nome: 'tx_adm_fiduciaria', label: 'Taxa adm fiduciária', tipo: 'text' }),
        ext({ nome: 'taxa_saida_pct', label: 'Taxa de saída (%)', tipo: 'percent', escala: 8 }),
        ext({ nome: 'aplicacao_inicial', label: 'Aplicação inicial (R$)', tipo: 'money', escala: 2 }),
        ext({ nome: 'aplicacao_minima', label: 'Aplicação mínima (R$)', tipo: 'money', escala: 2 }),
        ext({ nome: 'saldo_minimo', label: 'Saldo mínimo (R$)', tipo: 'money', escala: 2 }),
        ext({ nome: 'resgate_minimo', label: 'Resgate mínimo (R$)', tipo: 'money', escala: 2 }),
        ext({ nome: 'd_cotizacao_aplicacao', label: 'D+ cotização aplicação', tipo: 'text' }),
        ext({ nome: 'd_cotizacao_resgate', label: 'D+ cotização resgate', tipo: 'text' }),
        ext({ nome: 'd_pgto_resgate', label: 'D+ pgto resgate', tipo: 'text' }),
        ext({ nome: 'saida_antecipada_cotizacao', label: 'Saída antecipada – cotiz.', tipo: 'text' }),
        ext({ nome: 'saida_antecipada_pgto', label: 'Saída antecipada – pgto.', tipo: 'text' }),
        ext({ nome: 'administrador_id', label: 'Administrador', tipo: 'entidade', entidade: 'prestador' }),
        ext({ nome: 'gestor_id', label: 'Gestor', tipo: 'entidade', entidade: 'prestador' }),
        ext({ nome: 'custodiante_id', label: 'Custodiante', tipo: 'entidade', entidade: 'prestador' }),
        ext({ nome: 'auditor_id', label: 'Auditor', tipo: 'entidade', entidade: 'prestador' }),
        ext({ nome: 'calendario', label: 'Calendário', tipo: 'select', opcoes: CALENDARIO }),
        ext({ nome: 'status_aplicacao', label: 'Status de aplicação', tipo: 'select', opcoes: STATUS_APLIC }),
        ext({ nome: 'regime_juros', label: 'Regime de juros', tipo: 'select', opcoes: REGIME_JUROS }),
        ext({ nome: 'instrucao_normativa', label: 'Instrução normativa', tipo: 'text', hint: 'Ex.: CVM 175, CVM 160' }),
        ext({ nome: 'taxa_saida', label: 'Tem taxa de saída', tipo: 'checkbox' }),
      ],
    },
  ],
};

export const TIPOS_ATIVO: Record<string, TipoAtivoDef> = {
  CRI_CRA, DEBENTURE, LCI_LCA, CDB_LF, TITULO_PUBLICO, LAM, ACAO, FUTURO, FUNDO,
};

export const TIPO_OPCOES: Opt[] = Object.values(TIPOS_ATIVO).map(
  (t) => ({ value: t.tipo, label: `${t.grupoLabel} · ${t.label}` }),
);

export const getTipoDef = (tipo: string): TipoAtivoDef | undefined => TIPOS_ATIVO[tipo];

export const campoVisivel = (c: CampoDef, dados: Record<string, any>): boolean => {
  if (!c.habilitarQuando) return true;
  const atual = dados[c.habilitarQuando.campo];
  const alvo = c.habilitarQuando.valor;
  return Array.isArray(alvo) ? alvo.includes(atual) : atual === alvo;
};

export const dadosIniciais = (def: TipoAtivoDef): Record<string, any> => {
  const d: Record<string, any> = {};
  for (const s of def.secoes) for (const c of s.campos) if (c.default !== undefined) d[c.nome] = c.default;
  return d;
};

export interface GrupoTipos { grupo: string; grupoLabel: string; tipos: { tipo: string; label: string }[]; }
export const TIPOS_POR_GRUPO: GrupoTipos[] = (() => {
  const map = new Map<string, GrupoTipos>();
  for (const t of Object.values(TIPOS_ATIVO)) {
    if (!map.has(t.grupo)) map.set(t.grupo, { grupo: t.grupo, grupoLabel: t.grupoLabel, tipos: [] });
    map.get(t.grupo)!.tipos.push({ tipo: t.tipo, label: t.label });
  }
  return Array.from(map.values());
})();
